import React, {
  useState,
  useCallback,
  memo,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import {
  DateField,
  MenuTrigger,
  ActionButton,
  View,
  Flex,
  Text,
  Calendar,
  RangeCalendar,
  Item,
  TextField
} from '@adobe/react-spectrum';
import CalendarIcon from '@spectrum-icons/workflow/Calendar';
import { parseDate, CalendarDate } from '@internationalized/date';
import { FormField } from '../../store/slice';
import { CalendarRange } from '../../../Fields/Calendar';
import { calendarDateToISOString, isValidDateString } from '../utils/dateUtils';
import styles from '../FormField.module.scss';

interface DateComponentProps {
  field: FormField;
  value: any;
  onChange: (name: string, value: any) => void;
  hideLabel?: boolean;
  minimal?: boolean;
  validationState?: 'valid' | 'invalid';
  errorMessage?: string;
  disableTBD?: boolean;
  disableNA?: boolean;
  // Control whether TBD/NA quick options are visible in the calendar
  showTBDNA?: boolean;
  // Calendar row visibility controls (from milestone config)
  hideMonthList?: boolean;
  hideQuarterList?: boolean;
  hideYearList?: boolean;
}

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/**
 * At most one DateComponent's calendar popover should be open at a time across
 * the whole app — otherwise two open side by side (e.g. "Sent On" + "Due Date"
 * in the same form) overlap. Each instance still owns its own `isDatePickerOpen`
 * state (no shared context/props needed); this just tracks which instance's
 * closer to call when a different one opens. Relying solely on each instance's
 * own click-outside listener doesn't reliably close the previous picker before
 * the next one's own open handler runs, since the two are independent native
 * listeners with no ordering guarantee across component instances.
 */
let closeActiveDatePicker: (() => void) | null = null;

/**
 * Component for rendering date field types (date, datepicker)
 */
export const DateComponent: React.FC<DateComponentProps> = memo(
  ({
    field,
    value,
    onChange,
    hideLabel,
    minimal,
    validationState,
    errorMessage,
    disableTBD,
    disableNA,
    showTBDNA,
    hideMonthList,
    hideQuarterList,
    hideYearList
  }) => {
    const effectiveValidationState =
      validationState === 'invalid' ? 'invalid' : undefined;
    const isMarketingDate = field?.name === 'marketingDate';
    const isEffectiveDate = field?.name === 'effectiveDate';
    const fieldDefaultValue = (field as any)?.defaultValue as
      | string
      | undefined;

    // Update getDisplayValue to handle special values
    const getDisplayValue = () => {
      if (!value) return '';

      // Add special value handling
      if (typeof value === 'object' && value.type === 'special') {
        return value.value;
      }

      // Handle string values that might be JSON
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === 'object') {
              if (
                parsed.type === 'months' &&
                Array.isArray(parsed.months) &&
                parsed.months.length > 0
              ) {
                return String(parsed.months[0]);
              } else if (
                parsed.type === 'quarters' &&
                Array.isArray(parsed.quarters) &&
                parsed.quarters.length > 0
              ) {
                return String(parsed.quarters[0]);
              }
            }
          } catch (e) {
            // If JSON parse fails, treat as regular string
            return value;
          }
        }
        return value;
      }

      // Keep existing display value logic with timezone-safe formatting
      if (typeof value === 'object') {
        if (value.type === 'date' && value.date) {
          // Check if value.date is itself an object (nested structure)
          if (typeof value.date === 'object') {
            if (
              value.date.type === 'months' &&
              Array.isArray(value.date.months) &&
              value.date.months.length > 0
            ) {
              return String(value.date.months[0]);
            } else if (
              value.date.type === 'quarters' &&
              Array.isArray(value.date.quarters) &&
              value.date.quarters.length > 0
            ) {
              return String(value.date.quarters[0]);
            }
          }
          // Keep date in YYYY-MM-DD format for consistency with manual input
          else if (
            typeof value.date === 'string' &&
            isValidDateString(value.date)
          ) {
            return value.date;
          }
          // Fallback to original value if parsing fails
          return typeof value.date === 'string' ? value.date : '';
        } else if (
          value.type === 'months' &&
          Array.isArray(value.months) &&
          value.months.length > 0
        ) {
          return String(value.months[0]);
        } else if (
          value.type === 'quarters' &&
          Array.isArray(value.quarters) &&
          value.quarters.length > 0
        ) {
          return String(value.quarters[0]);
        }
      }

      return '';
    };

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    // Stable across re-renders (created once) so the identity check in the
    // effect below can tell "this instance" apart from any other.
    const closeThisPickerRef = useRef(() => setIsDatePickerOpen(false));

    // Opening this picker closes whichever other one was already open;
    // closing it (via its own click-outside/selection handlers) releases
    // ownership so the next picker to open doesn't try to close a stale ref.
    useEffect(() => {
      if (isDatePickerOpen) {
        if (
          closeActiveDatePicker &&
          closeActiveDatePicker !== closeThisPickerRef.current
        ) {
          closeActiveDatePicker();
        }
        closeActiveDatePicker = closeThisPickerRef.current;
      } else if (closeActiveDatePicker === closeThisPickerRef.current) {
        closeActiveDatePicker = null;
      }
    }, [isDatePickerOpen]);

    // Release ownership on unmount while open (e.g. the modal closes) so a
    // stale reference is never left behind for the next picker to skip.
    useEffect(() => {
      const closeThisPicker = closeThisPickerRef.current;
      return () => {
        if (closeActiveDatePicker === closeThisPicker) {
          closeActiveDatePicker = null;
        }
      };
    }, []);

    const [selectedDate, setSelectedDate] = useState<CalendarDate | undefined>(
      parseInitialValue()
    );
    const [inputValue, setInputValue] = useState(() => {
      const initialDisplay = getDisplayValue();
      return initialDisplay;
    });
    const [formatError, setFormatError] = useState<string | null>(null);
    const [currentSpecialValue, setCurrentSpecialValue] = useState<
      'TBD' | 'N/A' | null
    >(
      value?.type === 'special'
        ? value.value
        : fieldDefaultValue === 'N/A'
        ? 'N/A'
        : fieldDefaultValue === 'TBD'
        ? 'TBD'
        : null
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);
    // Whether the calendar popover should open upward (anchored above the
    // input) instead of its default below. `.milestoneCalendar` is `position:
    // absolute` inside whatever scrollable ancestor the consuming modal uses
    // (e.g. EditMilestoneModal's `.figma-modal-content.is-scrollable`) —
    // `overflow-y: auto` on that ancestor clips it whenever it extends past
    // the CURRENTLY VISIBLE scrolled viewport, regardless of the ancestor's
    // full scrollable height. Measured fresh every time the calendar opens
    // (its content — quarter/month rows, TBD/NA toggles — varies by props).
    const [calendarOpensUpward, setCalendarOpensUpward] = useState(false);
    // Whether the calendar should anchor its RIGHT edge to the trigger's right
    // edge (extending leftward) instead of the default left-aligned anchor.
    // Same reasoning as `calendarOpensUpward` but for the horizontal axis: a
    // right-column field (e.g. EditMilestoneModal's Due Date) sits flush
    // against `.figma-modal-content.is-scrollable`'s right edge — which only
    // exists once Send-for-Approval expands the form far enough to need
    // scrolling — and that ancestor's `overflow-x: hidden` clips the
    // calendar's right side when it's wider than the remaining space, instead
    // of letting it render fully like it does in every other state (where no
    // such clipping ancestor exists at all, so this stays false and nothing
    // about that view changes).
    const [calendarAlignRight, setCalendarAlignRight] = useState(false);
    // Extra left offset for the DEFAULT (non-flipped) case — mirrors
    // `calendarAlignRight`'s reasoning for the opposite column: a left-column
    // field (e.g. Sent On) sits flush against `.figma-modal-content
    // .is-scrollable`'s LEFT edge for the same structural reason Due Date sits
    // flush against its right edge (both columns are `flex: 1` with no margin
    // between them and their shared ancestor). Left-aligning at `left: 0`
    // there lands the calendar flush against that same clipping boundary,
    // leaving zero room for .calendar-container's box-shadow on that side.
    // Stays 0 outside that state (no clipping ancestor found), so nothing
    // about that view changes.
    const [calendarLeftOffset, setCalendarLeftOffset] = useState(0);

    useLayoutEffect(() => {
      if (!isDatePickerOpen) return;
      const triggerEl = containerRef.current;
      const calendarEl = calendarRef.current;
      if (!triggerEl || !calendarEl) return;

      const triggerRect = triggerEl.getBoundingClientRect();
      const calendarHeight = calendarEl.offsetHeight;
      const calendarWidth = calendarEl.offsetWidth;

      // The nearest scrolling ancestor clips this popover before the viewport
      // edge ever comes into play — bound available space to it, not just
      // `window.innerHeight`.
      let scrollAncestor: HTMLElement | null = triggerEl.parentElement;
      while (scrollAncestor) {
        const overflowY = window.getComputedStyle(scrollAncestor).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') break;
        scrollAncestor = scrollAncestor.parentElement;
      }
      const lowerBound = scrollAncestor
        ? Math.min(
            window.innerHeight,
            scrollAncestor.getBoundingClientRect().bottom
          )
        : window.innerHeight;
      const upperBound = scrollAncestor
        ? Math.max(0, scrollAncestor.getBoundingClientRect().top)
        : 0;

      const spaceBelow = lowerBound - triggerRect.bottom;
      const spaceAbove = triggerRect.top - upperBound;

      setCalendarOpensUpward(
        spaceBelow < calendarHeight && spaceAbove > spaceBelow
      );

      // Same walk as above, but for the nearest ancestor that clips
      // horizontally — checks `hidden` too (not just `auto`/`scroll`), since
      // that's the actual value `.figma-modal-content.is-scrollable` uses.
      // Outside that state there's no such ancestor, so this walk finds
      // nothing, bounds to the full window width, and the flip below never
      // triggers — leaving every other view untouched.
      let scrollAncestorX: HTMLElement | null = triggerEl.parentElement;
      while (scrollAncestorX) {
        const overflowX = window.getComputedStyle(scrollAncestorX).overflowX;
        if (
          overflowX === 'auto' ||
          overflowX === 'scroll' ||
          overflowX === 'hidden'
        ) {
          break;
        }
        scrollAncestorX = scrollAncestorX.parentElement;
      }
      const rightBound = scrollAncestorX
        ? Math.min(
            window.innerWidth,
            scrollAncestorX.getBoundingClientRect().right
          )
        : window.innerWidth;
      const leftBound = scrollAncestorX
        ? Math.max(0, scrollAncestorX.getBoundingClientRect().left)
        : 0;

      const spaceRightFromLeftAlign = rightBound - triggerRect.left;
      const spaceLeftFromRightAlign = triggerRect.right - leftBound;

      // Matches the `right: 8` clearance actually applied below when flipped —
      // require that same margin to be available before committing to the
      // flip, so the decision and the applied position agree on how much room
      // the calendar (plus its box-shadow) actually needs.
      const SHADOW_CLEARANCE = 8;
      setCalendarAlignRight(
        spaceRightFromLeftAlign < calendarWidth &&
          spaceLeftFromRightAlign >= calendarWidth + SHADOW_CLEARANCE
      );

      // Default (left-aligned) case: how much room sits to the left of the
      // trigger within the same clipping ancestor. When that's less than the
      // shadow needs (structurally ~0 for a left-column field once
      // `.is-scrollable` applies, since it's flush against this exact
      // boundary), nudge the calendar right by just enough to clear it.
      const spaceLeftOfTrigger = triggerRect.left - leftBound;
      setCalendarLeftOffset(Math.max(0, SHADOW_CLEARANCE - spaceLeftOfTrigger));
    }, [isDatePickerOpen]);

    // Helper to extract defaultQuarter from current value
    const getDefaultQuarter = ():
      | { quarter: number; year: number }
      | undefined => {
      if (!value) return undefined;

      // Handle object format: {type: 'quarters', quarters: ['Q2 2026']}
      if (typeof value === 'object' && value !== null) {
        if (
          value.type === 'quarters' &&
          Array.isArray(value.quarters) &&
          value.quarters.length > 0
        ) {
          // Parse "Q2 2026" format
          const match = value.quarters[0].match(/Q(\d)\s+(\d{4})/);
          if (match) {
            return {
              quarter: parseInt(match[1], 10),
              year: parseInt(match[2], 10)
            };
          }
        }
      }

      // Handle string format: "Q1 2026" (from EditMilestoneModal)
      if (typeof value === 'string') {
        const trimmed = value.trim();
        const match = trimmed.match(/Q(\d)\s+(\d{4})/);
        if (match) {
          return {
            quarter: parseInt(match[1], 10),
            year: parseInt(match[2], 10)
          };
        }
      }

      return undefined;
    };

    // Helper to extract defaultMonth from current value
    const getDefaultMonth = (): { month: string; year: number } | undefined => {
      if (!value) return undefined;

      // Handle object format: {type: 'months', months: ['Feb 2026']}
      if (typeof value === 'object' && value !== null) {
        if (
          value.type === 'months' &&
          Array.isArray(value.months) &&
          value.months.length > 0
        ) {
          // Parse "Feb 2026" format
          const match = value.months[0].match(/([A-Za-z]+)\s+(\d{4})/);
          if (match) {
            return { month: match[1], year: parseInt(match[2], 10) };
          }
        }
      }

      // Handle string format: "Feb 2026" (from EditMilestoneModal)
      if (typeof value === 'string') {
        const trimmed = value.trim();
        const match = trimmed.match(/([A-Za-z]+)\s+(\d{4})/);
        if (match) {
          return { month: match[1], year: parseInt(match[2], 10) };
        }
      }

      return undefined;
    };

    // Set default value to N/A when component mounts and no value is set
    useEffect(() => {
      if (field.type === 'datepicker' && !value) {
        if (fieldDefaultValue === 'N/A' || fieldDefaultValue === 'TBD') {
          const defaultValue = {
            type: 'special',
            value: fieldDefaultValue as 'N/A' | 'TBD'
          };
          onChange(field.name, defaultValue);
          setInputValue(fieldDefaultValue);
          setCurrentSpecialValue(fieldDefaultValue as 'N/A' | 'TBD');
        } else if (
          typeof fieldDefaultValue === 'string' &&
          isValidDateString(fieldDefaultValue)
        ) {
          try {
            const calendarDate = parseDate(fieldDefaultValue);
            setSelectedDate(calendarDate);
            // Keep input in YYYY-MM-DD for consistency
            setInputValue(fieldDefaultValue);
            onChange(field.name, {
              type: 'date',
              date: fieldDefaultValue
            });
            setCurrentSpecialValue(null);
          } catch (e) {
            // Ignore invalid defaultValue silently
          }
        } else {
          // Treat missing or empty defaultValue as no default
        }
      }
    }, []); // Only run once on mount

    // Handle click outside to close calendar
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        // Check if click is inside calendar elements (quarters/months/years buttons)
        const isCalendarElement =
          target.closest('.quarters-list') ||
          target.closest('.months-list') ||
          target.closest('.years-list') ||
          target.closest('.calendar-container') ||
          target.closest('.milestoneCalendar');

        if (isCalendarElement) {
          // Don't close if clicking calendar elements
          return;
        }

        if (containerRef.current && !containerRef.current.contains(target)) {
          setIsDatePickerOpen(false);
        }
      };

      if (isDatePickerOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isDatePickerOpen]);

    // Re-evaluate initial value whenever 'value' changes (e.g., when reopening modal)
    useEffect(() => {
      setSelectedDate(parseInitialValue());
      const displayVal = getDisplayValue();
      setInputValue(displayVal);

      // Sync currentSpecialValue with the actual value
      if (value?.type === 'special') {
        setCurrentSpecialValue(value.value);
      } else {
        setCurrentSpecialValue(null);
      }
    }, [value]);

    // Keep existing parseInitialValue function
    function parseInitialValue() {
      if (!value) return undefined;

      try {
        if (typeof value === 'string') {
          // If the string is a JSON months/quarters payload, don't parse as date
          const trimmed = value.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            return undefined;
          }

          // Check if it's a month-year format (e.g., "Feb 2026", "Mar 2026", "Jan 2026")
          // These formats cannot be parsed as ISO 8601 dates, so return undefined
          const monthYearPattern = /^[A-Za-z]{3,}\s+\d{4}$/;
          if (monthYearPattern.test(trimmed)) {
            return undefined; // Month-year formats are not CalendarDate objects
          }

          // Check if it's a quarter format (e.g., "Q1 2026")
          const quarterPattern = /^Q[1-4]\s+\d{4}$/;
          if (quarterPattern.test(trimmed)) {
            return undefined; // Quarter formats are not CalendarDate objects
          }

          // Only parse if it looks like an ISO 8601 date (YYYY-MM-DD)
          if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return parseDate(value);
          }

          // For other formats, return undefined to avoid parsing errors
          return undefined;
        } else if (typeof value === 'object') {
          if (
            value.type === 'date' &&
            value.date &&
            typeof value.date === 'string'
          ) {
            // Check if it's a month-year or quarter format before parsing
            const dateStr = value.date.trim();
            const monthYearPattern = /^[A-Za-z]{3,}\s+\d{4}$/;
            const quarterPattern = /^Q[1-4]\s+\d{4}$/;

            if (
              monthYearPattern.test(dateStr) ||
              quarterPattern.test(dateStr)
            ) {
              return undefined; // Month-year and quarter formats are not CalendarDate objects
            }

            // Only parse if it looks like an ISO 8601 date
            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return parseDate(value.date);
            }

            return undefined;
          }
          // months/quarters are not parsed into CalendarDate
          return undefined;
        }
      } catch (error) {
        console.error('Error parsing date:', error);
      }
      return undefined;
    }

    // Handle manual date input
    const handleInputChange = (value: string) => {
      setInputValue(value);
      setFormatError(null); // Clear any existing error

      // When input is empty or special values
      if (!value || value === 'N/A' || value === 'TBD') {
        // Send empty value to form to trigger validation
        onChange(field.name, '');
        return;
      }

      // Try to parse the input as a date using timezone-safe validation
      try {
        if (isValidDateString(value)) {
          const calendarDate = parseDate(value); // Can directly use the YYYY-MM-DD string
          setSelectedDate(calendarDate);

          // When typing, keep the YYYY-MM-DD format in the input
          setInputValue(value);

          // Send the validated date string to the form
          onChange(field.name, {
            type: 'date',
            date: value
          });
        } else if (value.length > 0) {
          setFormatError('Please enter date in YYYY-MM-DD format');
        }
      } catch (error) {
        console.error('Error parsing input date:', error);
        setFormatError('Please enter a valid date in YYYY-MM-DD format');
      }
    };

    // Keep existing handleDateChange with added special value support
    const handleDateChange = useCallback(
      (
        newDate: CalendarDate | null,
        isQuarterSelection?: boolean,
        selectedQuarter?: { quarter: number; year: number },
        isMonthSelection?: boolean,
        selectedMonth?: { month: string; year: number },
        specialValue?: 'TBD' | 'N/A' | null
      ) => {
        if (field.type !== 'datepicker') return;

        setFormatError(null); // Clear any existing error

        // Handle special value first
        if (specialValue !== undefined) {
          if (specialValue === null) {
            // Clear the value when switching off N/A
            onChange(field.name, '');
            setInputValue('');
            setSelectedDate(undefined);
            setCurrentSpecialValue(null);
            setIsDatePickerOpen(false);
            // Remove focus to clear the blue border
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
            return;
          }
          const newValue = {
            type: 'special',
            value: specialValue
          };
          onChange(field.name, newValue);
          setInputValue(specialValue);
          setCurrentSpecialValue(specialValue);
          setIsDatePickerOpen(false);
          // Remove focus to clear the blue border
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          return;
        }

        // If no date is selected and no special value, clear everything
        if (!newDate) {
          onChange(field.name, '');
          setInputValue('');
          setSelectedDate(undefined);
          setCurrentSpecialValue(null);
          setIsDatePickerOpen(false);
          // Remove focus to clear the blue border
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          return;
        }

        // Keep existing date handling logic
        setSelectedDate(newDate);
        try {
          if (isQuarterSelection && selectedQuarter) {
            const newValue = {
              type: 'quarters',
              quarters: [`Q${selectedQuarter.quarter} ${selectedQuarter.year}`]
            };
            onChange(field.name, newValue);
            setInputValue(newValue.quarters[0]);
            setSelectedDate(undefined);
            setCurrentSpecialValue(null);
          } else if (isMonthSelection && selectedMonth) {
            const newValue = {
              type: 'months',
              months: [`${selectedMonth.month} ${selectedMonth.year}`]
            };
            onChange(field.name, newValue);
            setInputValue(newValue.months[0]);
            setSelectedDate(undefined);
            setCurrentSpecialValue(null);
          } else {
            // Use timezone-safe date serialization
            const serializedDate = calendarDateToISOString(newDate);
            const newValue = {
              type: 'date',
              date: serializedDate
            };
            onChange(field.name, newValue);

            // Keep the display in YYYY-MM-DD format for consistency with manual input
            // This allows users to copy and paste the displayed value
            setInputValue(serializedDate);
            setCurrentSpecialValue(null);
          }
          setIsDatePickerOpen(false);
          // Remove focus to clear the blue border
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        } catch (error) {
          console.error('Error handling date change:', error);
        }
      },
      [field, onChange]
    );

    // Standard date field remains unchanged
    if (field.type === 'date') {
      return (
        <DateField
          UNSAFE_className={styles.dateField}
          key={field.id}
          label={field.label}
          value={value || ''}
          onChange={val => onChange(field.name, val)}
          isRequired={field.required}
          data-testid={`date-field-${field.name
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`}
        />
      );
    }

    // Date picker with calendar
    if (field.type === 'datepicker') {
      return (
        <div
          data-testid={`date-picker-container-${field.name
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`}
          className={styles.datePickerContainer}
          id="abcd"
        >
          {!hideLabel && (
            <Text UNSAFE_className={styles.fieldLabel}>
              {field.label}
              {field.required && <Text> *</Text>}
            </Text>
          )}
          <div
            ref={containerRef}
            style={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            <div
              style={{ display: 'flex', width: '100%' }}
              className={styles.datePickerInputGroup}
            >
              <TextField
                value={
                  typeof inputValue === 'string'
                    ? inputValue
                    : String(inputValue || '')
                }
                onChange={handleInputChange}
                placeholder="YYYY-MM-DD"
                UNSAFE_className={styles.dateInput}
                aria-label={field.label}
                validationState={effectiveValidationState}
              />
              <ActionButton
                UNSAFE_className={`${styles.calendarIconButton} ${
                  effectiveValidationState === 'invalid'
                    ? styles.calendarIconButtonInvalid
                    : ''
                }`}
                aria-label="Open calendar"
                onPress={() => setIsDatePickerOpen(prev => !prev)}
              >
                <CalendarIcon />
              </ActionButton>
            </div>
            {isDatePickerOpen && (
              <div
                className="milestoneCalendar"
                ref={calendarRef}
                style={{
                  ...(calendarOpensUpward
                    ? { top: 'auto', bottom: '40px' }
                    : undefined),
                  ...(calendarAlignRight
                    ? // Not `right: 0` — that lands flush against the same
                      // clipping ancestor edge that made this flip necessary,
                      // leaving zero room for .calendar-container's own
                      // box-shadow to render and clipping it off on that side.
                      { left: 'auto', right: 8 }
                    : calendarLeftOffset > 0
                    ? { left: calendarLeftOffset }
                    : undefined)
                }}
              >
                <CalendarRange
                  defaultDate={selectedDate || undefined}
                  defaultQuarter={getDefaultQuarter()}
                  defaultMonth={getDefaultMonth()}
                  onCalendarDateChange={handleDateChange}
                  mode="single"
                  skipTriggerButton={true}
                  hideYearList={hideYearList || true}
                  hideQuarterList={hideQuarterList}
                  hideMonthList={hideMonthList}
                  showTBDNA={
                    showTBDNA !== undefined
                      ? Boolean(showTBDNA)
                      : isMarketingDate
                      ? true
                      : isEffectiveDate
                      ? true
                      : true
                  }
                  defaultSpecialValue={currentSpecialValue}
                  disableNA={
                    disableNA !== undefined
                      ? disableNA
                      : isMarketingDate
                      ? true
                      : false
                  }
                  disableTBD={disableTBD}
                  data-testid={`date-picker-calendar-${field.name
                    .replace(/[^a-zA-Z0-9]/g, '-')
                    .toLowerCase()}`}
                  disablePastDate={false}
                />
              </div>
            )}
            {formatError && (
              <Text UNSAFE_className={styles.errorText}>{formatError}</Text>
            )}
          </div>
        </div>
      );
    }

    // Default case remains unchanged
    return null;
  }
);
